import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Avatar } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from '@/components/ui/toast'
import { getApiErrorMessage } from '@/services/api'
import { formatDateTime } from '@/lib/format'
import { cn } from '@/lib/utils'
import { useTaskComments, useAddTaskComment } from '@/hooks/useTasks'
import { usePermissions } from '@/hooks/usePermissions'

interface Props {
  taskId: string
  readOnly?: boolean
  variant?: 'default' | 'internalNotes'
  textareaClassName?: string
}

// Comments section (feedback #71). Add-only: a list of comments (author +
// timestamp + body) and a composer. No edit / delete / react affordances.
export function TaskComments({
  taskId,
  readOnly = false,
  variant = 'default',
  textareaClassName,
}: Props) {
  const { t } = useTranslation('tasks')
  const { canEdit } = usePermissions()
  const canPost = !readOnly && canEdit('tasks')
  const { data, isLoading, isError } = useTaskComments(taskId)
  const add = useAddTaskComment(taskId)
  const [body, setBody] = useState('')
  const internalNotes = variant === 'internalNotes'

  const comments = data?.data ?? []

  const onPost = async () => {
    const text = body.trim()
    if (!text) return
    try {
      await add.mutateAsync({ body: text })
      setBody('')
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('comments.addError')),
      })
    }
  }

  const composer = canPost ? (
    <div
      className={
        internalNotes
          ? 'space-y-3'
          : 'space-y-3 rounded-2xl border border-border/70 bg-muted/20 p-4'
      }
    >
      {!internalNotes && (
        <div className='space-y-1'>
          <p className='text-sm font-semibold text-foreground'>
            {t('comments.composeTitle')}
          </p>
          <p className='text-xs text-muted-foreground'>
            {t('comments.composeHint')}
          </p>
        </div>
      )}
      <Textarea
        value={body}
        maxLength={5000}
        rows={3}
        placeholder={
          internalNotes
            ? t('form.internalNotesPlaceholder')
            : t('comments.placeholder')
        }
        onChange={e => setBody(e.target.value)}
        className={cn(
          internalNotes
            ? 'min-h-[96px] rounded-xl border-[#edf0f5] bg-[#f6f8fc] px-4 py-3 text-sm font-normal shadow-none'
            : 'rounded-xl border-border bg-background px-4 py-3',
          textareaClassName
        )}
      />
      <div className='flex justify-end'>
        <Button
          className='rounded-xl px-4'
          disabled={add.isPending || !body.trim()}
          onClick={onPost}
        >
          {t('comments.post')}
        </Button>
      </div>
    </div>
  ) : null

  const list = isLoading ? (
    <div className='space-y-2'>
      <Skeleton className='h-12 w-full' />
      <Skeleton className='h-12 w-full' />
    </div>
  ) : isError ? (
    <p className='text-sm text-destructive'>{t('comments.loadError')}</p>
  ) : comments.length === 0 ? (
    internalNotes ? null : (
      <div className='rounded-2xl border border-dashed border-border bg-muted/10 px-4 py-8 text-center text-sm text-muted-foreground'>
        {t('comments.empty')}
      </div>
    )
  ) : (
    <ul className='space-y-3'>
      {comments.map(comment => (
        <li
          key={comment.id}
          className={
            internalNotes
              ? 'flex gap-3 rounded-2xl border border-[#edf0f5] bg-[#fafbfc] px-4 py-4 shadow-sm'
              : 'flex gap-3 rounded-2xl border border-border/70 bg-background px-4 py-4 shadow-sm'
          }
        >
          <Avatar
            className='h-8 w-8 shrink-0 text-xs'
            name={comment.author?.full_name ?? undefined}
          />
          <div className='min-w-0 flex-1'>
            <div className='flex flex-wrap items-center gap-x-2 gap-y-1'>
              <span className='text-sm font-semibold'>
                {comment.author?.full_name ?? t('comments.unknownAuthor')}
              </span>
              <span className='text-xs text-muted-foreground'>
                {formatDateTime(comment.created_at)}
              </span>
            </div>
            <p className='mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-foreground'>
              {comment.body}
            </p>
          </div>
        </li>
      ))}
    </ul>
  )

  if (internalNotes) {
    return (
      <div className='space-y-4'>
        {list}
        {composer}
      </div>
    )
  }

  return (
    <div className='space-y-4'>
      {composer}

      {/* List */}
      {list}
    </div>
  )
}

export default TaskComments
